import React, { useState, useEffect } from 'react';
import { useAuth, useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings, Save, Palette } from 'lucide-react';
import { CommissionScheduleCard, OperatingExpensesCard } from '@/components/FinanceSettingsCards';

function DemoControls({ api }) {
  const [busy, setBusy] = useState('');
  const [lastMsg, setLastMsg] = useState('');
  const run = async (action, path) => {
    setBusy(action);
    try {
      const r = await api.post(path);
      setLastMsg(r.data.message);
      if (action === 'reset') {
        ['recentEntitySearches', 'recent_searches'].forEach(k => localStorage.removeItem(k));
      }
    } catch (e) {
      setLastMsg(e?.response?.data?.detail || 'Action failed');
    }
    setBusy('');
  };
  return (
    <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(232,160,32,0.4)', borderLeft: '3px solid #E8A020' }} data-testid="demo-controls-card">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#B27516' }}>Demo Controls</h3>
      <p className="text-xs mb-3" style={{ color: '#5C6B7A' }}>Platform owner only. Reset clears session submissions and re-arms the CAT baseline (10 FNOLs · 8 auto-settled · 2 open). Policies, claims history, compliance and portfolio data are never touched.</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => run('reset', '/demo/reset')} disabled={!!busy} data-testid="demo-reset-btn"
          className="px-4 py-2 rounded-md text-xs font-semibold text-white disabled:opacity-50 hover:opacity-90 transition-opacity flex items-center gap-2"
          style={{ background: '#E8A020' }}>
          {busy === 'reset' && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          {busy === 'reset' ? 'Resetting...' : 'Reset Demo Environment'}
        </button>
        <button onClick={() => run('cat', '/demo/start-cat')} disabled={!!busy} data-testid="demo-start-cat-btn"
          className="px-4 py-2 rounded-md text-xs font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity flex items-center gap-2"
          style={{ color: '#B3261E', border: '1px solid rgba(179,38,30,0.4)' }}>
          {busy === 'cat' && <span className="w-3 h-3 border-2 border-[#B3261E] border-t-transparent rounded-full animate-spin" />}
          {busy === 'cat' ? 'Arming...' : 'Start CAT Demo'}
        </button>
      </div>
      {lastMsg && <p className="mt-2 text-xs font-medium" style={{ color: '#1D9E75' }} data-testid="demo-controls-msg">{lastMsg}</p>}
    </Card>
  );
}

function PublicSiteCard({ api }) {
  const [url, setUrl] = useState('');
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    api.get('/public/site').then(r => { setUrl(r.data.demo_video_url || ''); setSaved(r.data); }).catch(err => { console.error('API error:', err?.message); });
  }, [api]);
  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await api.put('/platform/public-site', { demo_video_url: url.trim() });
      setSaved(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save the link');
    }
    setBusy(false);
  };
  const kind = saved?.demo_video?.kind;
  return (
    <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)', borderLeft: '3px solid #1A4C7C' }} data-testid="public-site-card">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#1A4C7C' }}>Public site — demo video</h3>
      <p className="text-xs mb-3" style={{ color: '#5C6B7A' }}>
        The landing page's "Watch the 2-minute demo" button plays this recording. Paste a YouTube, Vimeo or Loom link, or a direct https:// .mp4 file. Leave it blank and the button shows the live scoring run with a "Request a live demo" prompt instead.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" className="text-sm" data-testid="public-site-video-url" />
        <Button onClick={save} disabled={busy} className="text-xs" style={{ background: '#1A4C7C' }} data-testid="public-site-save">{busy ? 'Saving…' : 'Save'}</Button>
      </div>
      {error && <p className="mt-2 text-xs" style={{ color: '#B3261E' }} data-testid="public-site-error">{error}</p>}
      {saved && !error && (
        <p className="mt-2 text-xs" style={{ color: kind ? '#1D9E75' : '#5C6B7A' }} data-testid="public-site-status">
          {kind ? `Live on the landing page as a ${kind === 'mp4' ? 'video file' : kind.charAt(0).toUpperCase() + kind.slice(1) + ' embed'}.` : 'No recording set — visitors see the live scoring run.'}
        </p>
      )}
    </Card>
  );
}

export default function MGAConfigPage() {
  const api = useApi();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/mga/config').then(r => setConfig(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/mga/config', config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error("API error:", err?.message);
    }
    setSaving(false);
  };

  const updateConfig = (key, value) => setConfig(prev => ({ ...prev, [key]: value }));

  if (loading) return <div className="space-y-4" data-testid="config-loading">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 rounded-sm" />)}</div>;

  const canEdit = ['platform_owner', 'mga_admin'].includes(user?.role);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="mga-config-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans', sans-serif" }}>MGA Configuration</h1>
        <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>White-label branding and appetite parameters</p>
      </div>

      {user?.role === 'platform_owner' && <DemoControls api={api} />}
      {canEdit && <PublicSiteCard api={api} />}

      <CommissionScheduleCard api={api} canEdit={canEdit} />
      <OperatingExpensesCard api={api} canEdit={canEdit} />

      {/* Brand Config */}
      <Card className="p-6 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-4">
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4" style={{ color: '#1A4C7C' }} />
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Brand Configuration</h3>
        </div>

        <div>
          <Label className="text-xs" style={{ color: '#5C6B7A' }}>MGA Name</Label>
          <Input data-testid="config-mga-name" value={config?.mga_name || ''} onChange={e => updateConfig('mga_name', e.target.value)}
            className="mt-1 rounded-sm" disabled={!canEdit} />
        </div>

        <div>
          <Label className="text-xs" style={{ color: '#5C6B7A' }}>Wording Link Domain</Label>
          <Input data-testid="config-wording-link-base" value={config?.wording_link_base || ''}
            onChange={e => updateConfig('wording_link_base', e.target.value)}
            placeholder="https://your-domain.com (blank = platform domain)"
            className="mt-1 rounded-sm" disabled={!canEdit} />
          <p className="text-[11px] mt-1" style={{ color: '#8896A8' }}>
            Public policy-wording links printed on schedules and emails use this base. Point it at your own domain once attached — existing links repoint automatically.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'primary_color', label: 'Primary' },
            { key: 'secondary_color', label: 'Secondary' },
            { key: 'accent_color', label: 'Accent' },
            { key: 'neutral_color', label: 'Neutral' },
          ].map(({ key, label }) => (
            <div key={key}>
              <Label className="text-xs" style={{ color: '#5C6B7A' }}>{label}</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" data-testid={`config-${key}`} value={config?.[key] || '#000000'}
                  onChange={e => updateConfig(key, e.target.value)}
                  className="w-8 h-8 rounded-sm border border-[rgba(136,150,168,0.2)] cursor-pointer" disabled={!canEdit} />
                <Input value={config?.[key] || ''} onChange={e => updateConfig(key, e.target.value)}
                  className="flex-1 rounded-sm font-mono text-xs" disabled={!canEdit} />
              </div>
            </div>
          ))}
        </div>

        {/* Color Preview */}
        <div className="flex gap-2 mt-2">
          {['primary_color', 'secondary_color', 'accent_color', 'neutral_color'].map(key => (
            <div key={key} className="h-8 flex-1 rounded-sm" style={{ background: config?.[key] || '#ccc' }} />
          ))}
        </div>
      </Card>

      {/* Appetite Config */}
      <Card className="p-6 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-4">
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4" style={{ color: '#1A4C7C' }} />
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Appetite Parameters</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Max Sum Insured ($)</Label>
            <Input data-testid="config-max-si" type="number" value={config?.appetite_max_si || ''} onChange={e => updateConfig('appetite_max_si', parseFloat(e.target.value))}
              className="mt-1 rounded-sm" disabled={!canEdit} />
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Max Turnover ($)</Label>
            <Input data-testid="config-max-turnover" type="number" value={config?.appetite_max_turnover || ''} onChange={e => updateConfig('appetite_max_turnover', parseFloat(e.target.value))}
              className="mt-1 rounded-sm" disabled={!canEdit} />
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>Binding Authority Limit ($)</Label>
            <Input data-testid="config-binding-limit" type="number" value={config?.binding_authority_limit || ''} onChange={e => updateConfig('binding_authority_limit', parseFloat(e.target.value))}
              className="mt-1 rounded-sm" disabled={!canEdit} />
          </div>
        </div>
      </Card>

      {canEdit && (
        <div className="flex items-center gap-3">
          <Button data-testid="save-config-button" onClick={handleSave} disabled={saving}
            className="rounded-sm font-semibold" style={{ background: '#1A4C7C' }}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Configuration'}
          </Button>
          {saved && <span className="text-sm" style={{ color: '#1D9E75' }}>Configuration saved successfully</span>}
        </div>
      )}
    </div>
  );
}
